// Selector de cadena de la pantalla Cobranza. Solo es un control cuando el usuario TIENE de dónde
// elegir: acotado a una cadena muestra un rótulo, y sin ninguna lo dice en vez de ofrecer un combo.
import { useQuery } from '@tanstack/react-query';
import { getRetailer, listRetailers } from '@/api/services/retail-org/retail-org.client';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/cn';
import { COLLECTIONS_KEY } from '../api/useCollections';
import { resolveRetailerScope } from '../retailerScope';
import type { ResolvedLabel } from '../types';

const FIELD =
    'h-7 w-full rounded-md border border-border bg-background px-2 text-[11.5px] text-foreground outline-none focus-visible:border-ring';

const CHOOSE_LABEL = 'Elige una cadena';

export const NO_RETAILER_MESSAGE = 'Tu usuario no tiene una cadena asignada.';

/** El directorio no devolvió el nombre de la cadena; el id NO se pinta como si lo fuera. */
export const UNRESOLVED_RETAILER_NOTE = 'cadena sin resolver';

/** Nombre de la cadena si `retail-org` lo resolvió; si no, el id con su leyenda. */
export function describeRetailer(retailerId: string, name: string | undefined): ResolvedLabel {
    if (name !== undefined && name !== '') return { text: name, note: null, isName: true };
    return { text: retailerId, note: UNRESOLVED_RETAILER_NOTE, isName: false };
}

/**
 * La cadena ya decidida: rótulo con su nombre, o el id cuando el directorio no la resolvió.
 * Lo comparte la barra de alcance de `/credit/customers`, que tiene el mismo caso LEVEL_1.
 */
export function RetailerLabel({ retailerId, hint }: { retailerId: string; hint?: string }) {
    const retailer = useQuery({
        queryKey: [COLLECTIONS_KEY, 'retailer', retailerId],
        queryFn: () => getRetailer(retailerId),
        staleTime: 300_000,
    });
    const label = describeRetailer(retailerId, retailer.data?.name);
    return (
        <p className="flex h-7 items-center gap-2 rounded-md bg-muted/40 px-2 text-[11.5px]">
            <span className={cn(label.isName ? 'font-medium' : 'font-mono text-[10.5px]')}>
                {label.text}
            </span>
            {label.note === null ? (
                <span className="truncate font-mono text-[10px] text-muted-foreground">
                    {retailerId}
                </span>
            ) : (
                <span className="text-[10px] italic text-muted-foreground">{label.note}</span>
            )}
            {hint === undefined ? null : (
                <span className="text-[10px] text-muted-foreground">{hint}</span>
            )}
        </p>
    );
}

/** Solo para quien abarca el silo (TENANT/PLATFORM): ahí `GET /retailers` sí responde. */
function RetailerDropdown({
    value,
    onChange,
}: {
    value: string;
    onChange: (retailerId: string) => void;
}) {
    const retailers = useQuery({
        queryKey: [COLLECTIONS_KEY, 'retailers'],
        queryFn: listRetailers,
        staleTime: 300_000,
    });
    return (
        <select
            aria-label="Cadena"
            className={FIELD}
            value={value}
            onChange={(event) => {
                onChange(event.target.value);
            }}
        >
            <option value="">{CHOOSE_LABEL}</option>
            {[...(retailers.data ?? [])]
                .sort((a, b) => a.name.localeCompare(b.name, 'es'))
                .map((retailer) => (
                    <option key={retailer.retailerId} value={retailer.retailerId}>
                        {retailer.name}
                    </option>
                ))}
        </select>
    );
}

export interface RetailerPickerProps {
    value: string;
    onChange: (retailerId: string) => void;
    /** Deep link `?retailer=`: manda sobre el rol del usuario y se muestra como rótulo, no como combo. */
    pinnedRetailerId?: string;
}

export function RetailerPicker({ value, onChange, pinnedRetailerId }: RetailerPickerProps) {
    const { roleAssignments } = useAuth();
    const { fixedRetailerId, canChoose } = resolveRetailerScope(roleAssignments);

    if (pinnedRetailerId !== undefined && pinnedRetailerId !== '') {
        return <RetailerLabel retailerId={pinnedRetailerId} hint="· fijada por el enlace" />;
    }
    if (fixedRetailerId !== null) return <RetailerLabel retailerId={fixedRetailerId} />;
    if (!canChoose) {
        return (
            <p className="rounded-md bg-muted/40 px-2 py-1.5 text-[11px] text-muted-foreground">
                {NO_RETAILER_MESSAGE}
            </p>
        );
    }
    return <RetailerDropdown value={value} onChange={onChange} />;
}
